import { useState, useEffect } from 'react'; // React Bootstrap imports
import {
  Accordion,
  Form,
  InputGroup,
  Row,
  Col,
  Container,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle,
  faSearch,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import { BeatLoader } from 'react-spinners';
import { useLocation, useNavigate } from 'react-router-dom';

// Import the FAQ data and types
import { fetchFAQData } from '../../utils/fetchFAQData';

import { FAQCategory, FAQItem, FAQData } from './faqTypes';

import './FAQ.css';

// Google Drive file ID from the shared link
const DRIVE_FILE_ID = '11L6TqLzlB3fiyh8TrWQjwSGRhoY6VZdB';

const FAQ = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State to hold FAQ data
  const [faqData, setFaqData] = useState<FAQData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch FAQ data on component mount
  useEffect(() => {
    const loadFAQData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchFAQData(DRIVE_FILE_ID);
        setFaqData(data);
      } catch (err) {
        setError('Failed to load FAQ data');
        console.error('Error loading FAQ data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFAQData();
  }, []);

  // State to hold the search query
  const [searchQuery, setSearchQuery] = useState('');

  // State to track which accordion item is open
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Filter data based on search query
  const filteredData =
    faqData?.faqData
      .map((category: FAQCategory) => {
        const filteredQuestions = category.questions.filter(
          (q: FAQItem) =>
            q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        );
        return { ...category, questions: filteredQuestions };
      })
      .filter((category: FAQCategory) => category.questions.length > 0) || [];

  // Find the correct active key in filtered data
  const getActiveKeyInFilteredData = () => {
    if (!activeKey || !faqData) return null;

    const [originalCategoryIndex, originalQuestionIndex] = activeKey
      .split('-')
      .map(Number);
    const originalCategory = faqData.faqData[originalCategoryIndex];
    const originalQuestion = originalCategory?.questions[originalQuestionIndex];

    if (!originalQuestion) return null;

    // Find this question in the filtered data
    let filteredCategoryIndex = 0;
    let filteredQuestionIndex = 0;

    for (let i = 0; i < filteredData.length; i++) {
      const category = filteredData[i];
      for (let j = 0; j < category.questions.length; j++) {
        if (category.questions[j].question === originalQuestion.question) {
          return `${i}-${j}`;
        }
        filteredQuestionIndex++;
      }
      filteredCategoryIndex++;
    }

    return null;
  };

  const filteredActiveKey = getActiveKeyInFilteredData();

  // Handle URL parameters for direct question access
  useEffect(() => {
    if (faqData) {
      const urlParams = new URLSearchParams(location.search);
      const questionParam = urlParams.get('q');

      if (questionParam) {
        // Decode the URL parameter
        const decodedQuestion = decodeURIComponent(questionParam);

        // Find the question in the data
        let foundKey: string | null = null;
        let foundQuestion: string | null = null;
        let bestMatchScore = 0;

        faqData.faqData.forEach((category: FAQCategory, categoryIndex) => {
          category.questions.forEach((question: FAQItem, questionIndex) => {
            const questionText = question.question.toLowerCase();
            const searchText = decodedQuestion.toLowerCase();

            // Create slug for exact slug matching
            const questionSlug = questionText
              .replace(/[^a-z0-9\s]/g, '')
              .replace(/\s+/g, '-')
              .substring(0, 50);

            // Calculate match score
            let matchScore = 0;

            // Exact slug match (highest priority)
            if (
              questionSlug.includes(searchText) ||
              searchText.includes(questionSlug)
            ) {
              matchScore = 100;
            }
            // Question contains search text
            else if (questionText.includes(searchText)) {
              matchScore = 50 + searchText.length; // Longer matches get higher score
            }
            // Search text contains question words
            else if (searchText.includes(questionText.split(' ')[0])) {
              matchScore = 25;
            }

            // Update if this is the best match so far
            if (matchScore > bestMatchScore) {
              bestMatchScore = matchScore;
              foundKey = `${categoryIndex}-${questionIndex}`;
              foundQuestion = question.question;
            }
          });
        });

        if (foundKey && foundQuestion && bestMatchScore > 0) {
          setActiveKey(foundKey);
          // Set search query to the partial text for better filtering
          setSearchQuery(decodedQuestion);
        }
      }
    }
  }, [faqData, location.search]);

  // Handle accordion selection (without URL rewriting)
  const handleAccordionSelect = (key: string | null) => {
    if (key && faqData) {
      const [filteredCategoryIndex, filteredQuestionIndex] = key
        .split('-')
        .map(Number);
      const filteredCategory = filteredData[filteredCategoryIndex];
      const filteredQuestion =
        filteredCategory?.questions[filteredQuestionIndex];

      if (filteredQuestion) {
        // Find the original key in the full data
        let originalKey: string | null = null;
        faqData.faqData.forEach((category: FAQCategory, categoryIndex) => {
          category.questions.forEach((question: FAQItem, questionIndex) => {
            if (question.question === filteredQuestion.question) {
              originalKey = `${categoryIndex}-${questionIndex}`;
            }
          });
        });

        if (originalKey) {
          setActiveKey(originalKey);
        }
      }
    } else {
      setActiveKey(null);
    }
  };

  // Update search query and URL as user types
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = e.target.value;
    setSearchQuery(newSearchQuery);

    // Update URL based on search query
    if (newSearchQuery.trim()) {
      const searchSlug = newSearchQuery
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      navigate(`/faq?q=${encodeURIComponent(searchSlug)}`, { replace: true });
    } else {
      // Clear URL parameter if search is empty
      navigate('/faq', { replace: true });
    }

    // Reset active key to first question when searching
    if (newSearchQuery.trim()) {
      setActiveKey('0-0');
    }
  };

  if (isLoading) {
    return (
      <Container className="faq-section">
        <h2 className="text-center mb-4">Frequently Asked Questions</h2>
        <div className="text-center my-5">
          <BeatLoader color="#0d6efd" size={15} margin={2} />
        </div>
      </Container>
    );
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>;
  }

  return (
    <div className="container mt-4 px-0">
      {/* FAQ Header Section */}
      <div className="faq-header mb-4">
        <Row className="align-items-center">
          <Col xs={12} md={6} className="mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="faq-icon me-2"
              />
              <div>
                <h3 className="mb-0">Frequently Asked Questions</h3>
                <p className="text-muted mb-0 d-md-none mt-2">
                  Find answers to common questions about our community and
                  services
                </p>
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <InputGroup className="faq-search">
              <InputGroup.Text className="bg-white border-end-0">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                id="faq-search"
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="border-start-0 ps-0"
              />
            </InputGroup>
          </Col>
        </Row>
      </div>

      {/* Check if there are no matching results */}
      {filteredData.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No matching questions found.</p>
        </div>
      ) : (
        <div>
          {filteredData.map((category: FAQCategory, categoryIndex) => (
            <div key={categoryIndex}>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon
                  icon={faBookOpen}
                  className="category-icon me-2"
                />
                <h4 className="mb-0">{category.category}</h4>
              </div>

              <Accordion
                activeKey={filteredActiveKey}
                onSelect={handleAccordionSelect}
              >
                {category.questions.map((item: FAQItem, index) => (
                  <Accordion.Item
                    eventKey={`${categoryIndex}-${index}`}
                    key={`${categoryIndex}-${index}`}
                  >
                    <Accordion.Header>{item.question}</Accordion.Header>
                    <Accordion.Body>{item.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>

              <div className="my-4"></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FAQ;
